import React, { useEffect, useRef, useState } from 'react';
import { Play, Volume2, VolumeX, Maximize2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { trackEvent, trackCta } from '../hooks/useAnalytics';

/**
 * Prominent "See How It Works" video section.
 *
 * UX:
 *   • Autoplays muted on loop so the visitor sees the product in motion
 *     the moment they scroll past the hero (highest-engagement format on
 *     mobile, IG traffic in particular).
 *   • Big "Tap to unmute" overlay because muted autoplay is the only thing
 *     mobile Safari/Chrome allow without explicit user gesture.
 *   • Fullscreen button for desktop.
 *   • Tracks `video_play`, `video_unmute`, `video_complete` so we know
 *     whether the explainer actually helps conversion.
 */
const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_local-biz-ads-2/artifacts/sjdkwphg_MBAP%20APPROVED%20VIDEO%201.mp4';

const HowItWorksVideo = ({ onPrimaryCta }) => {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Pause when off-screen, play when on-screen — saves data, especially on mobile.
  useEffect(() => {
    if (!videoRef.current || !sectionRef.current) return;
    const v = videoRef.current;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          v.play().catch(() => {});
          trackEvent('video_visible', { label: 'how_it_works' });
        } else if (!e.isIntersecting) {
          v.pause();
        }
      }
    }, { threshold: 0.35 });
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !muted;
    videoRef.current.muted = next;
    setMuted(next);
    if (!next) {
      // First unmute is the strongest engagement signal — track it.
      if (!hasInteracted) {
        trackEvent('video_unmute', { label: 'how_it_works' });
        setHasInteracted(true);
      }
      videoRef.current.play().catch(() => {});
    }
  };

  const fullscreen = () => {
    if (!videoRef.current) return;
    if (videoRef.current.requestFullscreen) videoRef.current.requestFullscreen();
    trackCta('video_fullscreen');
  };

  const onEnded = () => {
    if (completed) return;
    setCompleted(true);
    trackEvent('video_complete', { label: 'how_it_works' });
  };

  return (
    <section
      id="how-it-works-video"
      ref={sectionRef}
      className="relative py-12 sm:py-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white overflow-hidden"
      data-testid="how-it-works-video-section"
    >
      {/* Decorative glow */}
      <div aria-hidden className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-orange-500/20 blur-[120px]" />
        <div className="absolute -bottom-40 left-1/4 w-[500px] h-[500px] rounded-full bg-rose-500/15 blur-[100px]" />
      </div>

      <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-xs font-semibold uppercase tracking-wider text-orange-300 border border-orange-400/30">
            <Sparkles className="w-3.5 h-3.5" /> 60-second demo
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.1]">
            See it work in <span className="bg-gradient-to-r from-orange-400 to-rose-400 bg-clip-text text-transparent">60 seconds</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Watch an entire Meta-ready ad — copy, hashtags and image — get generated from a single sentence.
            <span className="block sm:inline text-emerald-300 font-semibold mt-1 sm:mt-0 sm:ml-2">No card needed to try.</span>
          </p>
        </div>

        {/* Video card — auto-sizes to the source aspect ratio so the full
            frame is always visible (no cropping). */}
        <div className="mt-10 relative group rounded-3xl overflow-hidden bg-slate-800 shadow-2xl ring-1 ring-white/10 max-w-3xl mx-auto">
          <video
            ref={videoRef}
            src={VIDEO_URL}
            preload="metadata"
            playsInline
            autoPlay
            muted
            loop
            poster=""
            onEnded={onEnded}
            onClick={toggleMute}
            className="w-full h-auto block cursor-pointer"
            data-testid="how-it-works-video-player"
          />

          {/* Subtle overlay only when muted — guides user to unmute */}
          {muted && (
            <button
              onClick={toggleMute}
              className="absolute inset-0 flex items-end justify-center pb-6 bg-gradient-to-t from-black/40 via-transparent to-transparent cursor-pointer"
              data-testid="video-unmute-overlay"
            >
              <span className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white/95 text-slate-900 text-sm font-semibold shadow-lg backdrop-blur">
                <Volume2 className="w-4 h-4" />
                Tap to unmute
              </span>
            </button>
          )}

          {/* Controls */}
          <div className="absolute top-3 right-3 flex items-center gap-2 opacity-90">
            <button
              onClick={toggleMute}
              className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center"
              aria-label={muted ? 'Unmute' : 'Mute'}
              data-testid="video-mute-toggle"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={fullscreen}
              className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur items-center justify-center hidden sm:flex"
              aria-label="Fullscreen"
              data-testid="video-fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* CTA below the video — peak engagement moment */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            onClick={() => { trackCta('video_below_primary'); onPrimaryCta?.(); }}
            size="lg"
            className="bg-white text-slate-900 hover:bg-slate-100 font-semibold rounded-xl px-7 h-13 sm:h-14 text-base shadow-xl"
            data-testid="video-below-primary-cta"
          >
            <Play className="w-4 h-4 mr-2 fill-current" />
            Make my first ad now — free 7-day trial
          </Button>
          <span className="text-xs text-slate-400">No card needed · cancel anytime · UK ASA-compliant</span>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksVideo;
